import Link from "next/link";
import { frameCostPerQrCode } from "@/app/(signed-in)/create/ai-qr-code-generator/constants";
import { fps } from "@/app/(signed-in)/create/text-to-video/helpers";
import { creatorMonthlyFrames } from "@/helpers/cost-to-frame-plan";
import { costMultiplierByType, maxNumberOfFrames, signupFrames } from "@/shared/constants";
import { dailyFramesReward } from "@/shared/project-type-metadata";

function CreateLink() {
  return (
    <a href="/create" className="link">
      Create
    </a>
  );
}

function MyPlanLink() {
  return (
    <Link href="/dashboard/my-plan" className="link">
      My Plan
    </Link>
  );
}
function PricingLink() {
  return (
    <Link href="/pricing" className="link">
      Pricing
    </Link>
  );
}

function EmailLink() {
  return (
    <a href="mailto:support@magichour.ai" className="link">
      support@magichour.ai
    </a>
  );
}

const planText = `Creator, Pro, or Business plan`;

export const faqData = {
  general: [
    {
      question: "Is Magic Hour free to use?",
      answer: `We offer ${signupFrames} frames for free so you can get to know Magic Hour and create your first clip. To create additional content beyond the ${signupFrames} frames, subscribe to our ${planText} or purchase a frame pack.`,
    },
    {
      question: "What is a frame?",
      answer:
        "A frame is how we measure usage. One frame is one image in the video. Depending on the type and amount of video you generate, it can cost between 12 - 30 frames per second of video.",
    },
    {
      question: "How many frames do I need?",
      answer:
        "As a rule of thumb, take the seconds of video you want and multiply it by its FPS (frames per second). A good starting point is 12 FPS for Animations, 24 FPS for Text-to-Video, and 30 FPS for Video-to-Video and Face Swap.",
    },
    {
      question: "Do my frames roll over each month?",
      answer:
        "Yes, your frames roll over each month and you will always have access to them. Note that your access to features however is determined by your subscription tier.",
    },
    {
      question: "Can I use my videos for commercial purposes?",
      answer:
        "Yes, the videos you create are yours, and you hold full commercial rights over them. Remember to respect copyright and intellectual property laws when using videos created with Magic Hour.",
    },
  ],
  pricing: [
    {
      question: "Do I need to enter a credit card to try Magic Hour?",
      answer: (
        <>
          No, Magic Hour&apos;s Free plan allows you to try our basic features for free. If
          you&apos;d like to use features not included in the Free plan, or get more frames after
          you&apos;ve run out, you can upgrade to a paid plan or purchase more frames.
        </>
      ),
    },
    {
      question: "How do I remove watermarks?",
      answer: "You can remove watermarks by purchasing a subscription or frame pack.",
    },
    {
      question: "What are frames?",
      answer: (
        <div className="space-y-4">
          <p>
            Frames are your currency on Magic Hour. They&apos;re deducted from your balance as you
            create images and videos.
          </p>
          <p>In general, one frame is equivalent to one image in a video.</p>
          <p>
            We chose this name because it makes thinking about usage easier. For example, you know
            that longer videos and higher FPS (frames per seconds) means more frames.
          </p>
          <p>For most modes, we charge one frame per frame in your video or image.</p>
          <p>For some modes, we charge more because they&apos;re more computationally expensive.</p>
          <p>
            When you&apos;re creating an image or video, we will always tell you exactly how many
            frames the generation will cost before you click render.
          </p>
        </div>
      ),
    },
    {
      question: "How do I get more frames?",
      answer: (
        <div className="space-y-4">
          <p>
            As a free user, you get an initial one-time {signupFrames} frames. Each day you visit
            the <CreateLink /> page, you can also claim an additional {dailyFramesReward} free
            frames regardless of your plan.
          </p>
          <p>
            As a paid subscriber, you receive a monthly frame allowance that gives you wider
            flexibility to use Magic Hour&apos;s premium features at your own pace. Importantly,
            these frames replenish at the beginning of each billing period and will never expire, so
            you can always save them for when your usage needs increase.
          </p>
        </div>
      ),
    },
    {
      question: "What happens if I run out of frames in my paid plan?",
      answer: (
        <div className="space-y-4">
          <p>When you run out of frames, you have three options:</p>
          <ul className="list-inside list-disc space-y-2">
            <li>
              You can wait for your next billing cycle to start. You can see when your next billing
              cycle starts in Next Invoice in <MyPlanLink />.
            </li>
            <li>
              You can purchase frame packs, which are one-time frame purchases in <PricingLink />.
              This allows you to &quot;top up&quot; your plan whenever you run out.
            </li>
            <li>
              You can start a new plan. If you switch from a monthly plan to an annual plan or
              vice-versa, the plan will start immediately. If you switch from a monthly plan to a
              different monthly plan or an annual plan to a different annual plan, your plan will
              start at the end of your billing cycle. However, if you want to start it immediately,
              you can click Upgrade Now in <MyPlanLink />.
            </li>
          </ul>
        </div>
      ),
    },
    {
      question: "Do frames roll over to the next month?",
      answer:
        "Yes, any unused frames in your balance will roll over to the next month, with no expiration. They are yours forever.",
    },
    {
      question: "What happens to my frames if I downgrade my plan?",
      answer:
        "If you downgrade your plan, you will keep all the frames you have, but your monthly allowance will adjust to your new plan's limit at the start of your next billing cycle.",
    },
    {
      question: "How do I cancel my subscription?",
      answer: (
        <>
          You can easily cancel your subscription any time in your <MyPlanLink /> page. After
          canceling, your subscription will end at the end of your billing cycle, and you will not
          be charged again.
        </>
      ),
    },
    {
      question: "Do I own the assets I create?",
      answer:
        "Yes, you retain full ownership, copyright, and other intellectual property rights of all the assets that you generate.",
    },
    {
      question: "Are my generations private?",
      answer: (
        <div className="space-y-4">
          <p>
            Yes, your generations are private from other users and Magic Hour. We will never access
            any of your assets without your explicit permission. You have the option to delete the
            generated asset after completion from our system. Make sure to download it first if
            you&apos;d like to keep it, as it&apos;s deleted forever.
          </p>
          <p>
            Assets that you upload to our platform, including images, audio, and videos are also
            private and are deleted within one week.
          </p>
        </div>
      ),
    },
    {
      question: "Can I get a refund?",
      answer: (
        <div className="space-y-4">
          If you purchased a subscription and aren&apos;t happy, we can offer you a refund if
          you&apos;ve only created one video. Otherwise, all services are non-refundable. If you
          have an issue with the product, please contact us in live chat or email us at{" "}
          <EmailLink />, and we&apos;d be happy to help you find a solution.
        </div>
      ),
    },
    {
      question: "Does the pricing include tax?",
      answer:
        "Pricing excludes tax. If we legally need to collect tax, it will be added at the point of payment with Stripe. If we do not currently charge tax in your country or region, and in the future need to, then this will be added to your recurring bill at that time.",
    },
    {
      question: "What payment methods do you accept?",
      answer: (
        <div className="space-y-4">
          <p>
            We accept credit cards and debit cards worldwide, including Visa, Mastercard, American
            Express, Discover, Diners Club, JCB, and China UnionPay.
          </p>

          <p>We accept other payment options depending on your country and region, including:</p>
          <ul className="list-inside list-disc space-y-2">
            <li>
              Wallets: Apple Pay, Cash App Pay, Google Pay, Amazon Pay, Link, Alipay, WeChat Pay
            </li>
            <li>Bank Redirects: Bancontact, EPS, giropay, iDEAL, Prszelewy24</li>
            <li>Buy Now, Pay Later: Affirm, Afterpay/Clearpay, Klarna</li>
          </ul>

          <p>We do not accept PayPal or cryptocurrency.</p>
        </div>
      ),
    },
    {
      question: "Can I use my plan for API access?",
      answer: (
        <div className="space-y-4">
          Yes, we offer API access for all users, with the same features and usage levels in your
          plan. Check out our{" "}
          <Link href="https://docs.magichour.ai" target="_blank" className="link">
            API Documentation
          </Link>{" "}
          to learn more. If you need any help with implementation or have questions, please reach
          out to us on live chat or email us at <EmailLink />. Our team is happy to assist you with
          your specific needs.
        </div>
      ),
    },
    {
      question: "What support is included with my subscription?",
      answer: (
        <>
          All users have access to email and live chat support. Paid subscribers get priority
          support. We aim to respond within 3 hours to paid users during the day, and all support
          requests within 24 hours. We&apos;re based in Pacific Time, so if we don&apos;t respond in
          a reasonable amount of time, we might be sleeping, so please give us some patience until
          we wake up.
        </>
      ),
    },
    {
      question: "Do you offer enterprise pricing or plans for teams?",
      answer: (
        <>
          Yes, we offer customized pricing for enterprise customers or teams with large-scale needs.
          Please contact us at <EmailLink /> to discuss your requirements.
        </>
      ),
    },
  ],
  aiClothesChanger: [
    {
      question: "What is an AI clothes changer?",
      answer:
        "An AI clothes changer uses artificial intelligence to edit clothing in photos, letting you try on outfits or styles digitally. It’s great for virtual styling, social media, and professional photo needs.",
    },
    {
      question: "My photo didn’t turn out well. What should I do?",
      answer: (
        <>
          For best results, use:
          <ul className="list-inside list-disc">
            <li>Outfit photos: white background, flat, single garment fully visible.</li>
            <li>Model photos: one person, front-facing, natural pose, no obstructions.</li>
          </ul>
        </>
      ),
    },
    {
      question: "What is the AI clothes changer used for?",
      answer:
        "It helps businesses, especially small ones, quickly update product images and videos for online stores, saving time and cost compared to traditional photoshoots.",
    },
    {
      question: "Does the AI clothes changer work for all clothing?",
      answer:
        "Yes! It supports shirts, pants, dresses, and more. Select Upper Body, Lower Body, or Dress based on the garment you’re swapping.",
    },
    // TODO: [acc] Update the values after running analysis on time + cost
    //{
    //  question: "How much does the AI clothes changer cost?",
    //  answer:
    //    "You get 3 free uses daily in our free tool. Signing up provides 400 frames and 100 free daily frames, allowing 20 additional swaps per day (one swap = 25 frames).",
    //},
    {
      question: "Can I use AI-generated photos commercially?",
      answer:
        "Yes, if you own the copyright for the model and outfit photos or use public domain images.",
    },
  ],
  animation: [
    {
      question: "Are Animations free to use?",
      answer: `Yes, Animation is a free mode, and we offer ${signupFrames} frames for free so you can get to know Magic Hour and create your first few clips. To create additional content beyond the ${signupFrames} frames, subscribe to our ${planText} or purchase a frame pack.`,
    },
    {
      question: "Can I upload an audio file?",
      answer: "Yes, simply drag and drop or click to upload a manual audio file.",
    },
    {
      question: "Can I upload an initial image?",
      answer:
        "Yes you can! The initial image will be used as the first frame of the video and will influence the remaining visuals. It's highly recommended if you have a style direction in mind.",
    },
    {
      question: "Can I generate previews of the video?",
      answer:
        "Not at the moment, but if you click render, you can see the frames as they render, and cancel at any time and receive all your frames back.",
    },
  ],
  faceSwap: [
    {
      question: "Is Face Swap free to use?",
      answer: `Yes! We offer ${signupFrames} frames for free so you can get to know Magic Hour and create your first Face Swap. To create additional content beyond the ${signupFrames} frames, subscribe to our ${planText} or purchase a frame pack.`,
    },
    {
      question: "How many Face Swaps can I make?",
      answer:
        "Each image in the video requires one frame, so this roughly works out to 900 frames for a 30 second video.",
    },
    {
      question: "Can I swap in multiple faces?",
      answer:
        "Yes, we support the ability to swap multiple faces by selecting the before/after face for each individual face. This feature is available by clicking Individual Faces in the dashboard version of our Face Swap tool.",
    },
    {
      question: "Can I upload my own video?",
      answer: "Yes you can! We support uploading your own video or using a YouTube URL.",
    },
    {
      question: "What photo should I use?",
      answer:
        "Choose a high resolution photo of your subject's face that shows the entire face clearly and in good lighting. Make sure it does not have any other faces and the face is not obstructed or cut off in any way.",
    },
  ],
  imageToImage: [
    {
      question: "How do I get access to Image-to-Image?",
      answer: "Image-to-Image is available to all users!",
    },
    {
      question: "What styles can I transform my image into?",
      answer:
        "Image-to-Image can transform your image into any styles, including anime, photograph, illustration, pop art, etc.",
    },
    {
      question: "What should I prompt?",
      answer:
        "Describe the style, subjects, and materials you'd like to see in the final image. You can adjust the prompt strength to choose how far to deviate from the original image.",
    },
    {
      question: "What do I need to create an image?",
      answer: "You need a image and either a prompt or a reference image.",
    },
  ],
  imageToVideo: [
    {
      question: "How do I get access to Magic Hour's Image-to-Video Generator?",
      answer: (
        <>
          The AI Image-to-Video feature is available exclusively to users subscribed to the $
          {planText}. If you are on a free or basic plan, you will need to upgrade to access this
          tool. Upgrading unlocks premium features, higher-quality video outputs, and enhanced
          editing capabilities. Learn more about our pricing and plans{" "}
          <a href="https://magichour.ai/pricing" className="link">
            here
          </a>
          .
        </>
      ),
    },
    {
      question: "How much does Image-to-Video cost?",
      answer: `Each frame in your image-to-video video costs ${costMultiplierByType.IMAGE_TO_VIDEO} frames from your balance. To get more frames, upgrade to our ${planText} or purchase a Frame pack.`,
    },
    {
      question: "Can I extend my video?",
      answer:
        "At the moment, we support video generation up to 60s, but we plan on enabling the ability to extend videos using the last frame of your video.",
    },
    {
      question: "I didn't capture the motion I'm looking for. What do I do?",
      answer:
        "Try using an image that captures the start of the motion you're going for. In the future, we plan on adding more motion controls. ",
    },
    {
      question: "How does the AI Image-to-Video tool work?",
      answer:
        "Our AI Image-to-Video tool takes a single input image and a short text prompt to generate a smooth animation that evolves from the original image. The system uses advanced diffusion and motion modeling to infer realistic transitions over time. It maintains the image’s core features while introducing creative and stylistic variations based on your prompt. The result is a short video clip that feels dynamic, cinematic, and visually coherent.",
    },
    {
      question: "Can I add audio or voiceover to my AI-generated video?",
      answer:
        "Currently, the Image-to-Video tool generates visuals only and does not support built-in audio or voiceover. However, you can download the video and add sound separately using any video editing software. We are exploring features that allow direct audio input or voiceover integration in future updates. For now, combining your video with external audio tools is the best approach.",
    },
    {
      question: "What types of videos can I create with an AI Image to Video generator?",
      answer:
        "You can create stylized animations, cinematic b-roll, fantasy or sci-fi scenes, animated portraits, product concepts, or mood-setting visuals. It's ideal for creators, marketers, artists, and storytellers looking to bring still images to life. The tool works best when paired with a creative prompt that guides the type of motion or transformation you want to see. It’s ideal for concepting, storytelling, and social media content that stands out.",
    },
  ],
  lipSync: [
    {
      question: "How can I lip sync a video using AI?",
      answer: (
        <div className="space-y-4">
          <p>Step 1: Prepare Your Files</p>
          <p>
            Upload your video file (MP4 or MOV) and audio file (MP3) to the Magic Hour platform.
          </p>
          <p>Step 2: Upload Your Content</p>
          <p>
            Navigate to the Lip Sync tool and upload both files through our intuitive interface.
          </p>
          <p>Step 3: Adjust Settings</p>
          <p>
            Choose your preferred synchronization settings and language options for optimal results.
          </p>
          <p>Step 4: Generate Your Lip Sync</p>
          <p>
            Click &quot;Generate&quot; and let our AI work its magic to perfectly align your audio
            with the video.
          </p>
          <p>Step 5: Review and Download</p>
          <p>
            Preview your synced video, make any necessary adjustments, and download the final
            result.
          </p>
        </div>
      ),
    },
    {
      question: "What is AI Lip Syncing?",
      answer:
        "AI Lip Syncing is advanced technology that automatically synchronizes a subject's lip and facial movements in video with any audio track. Our cutting-edge algorithms analyze audio timing and facial features to create perfectly synchronized, natural-looking results that are indistinguishable from originally recorded video.",
    },
    {
      question: "How do I get access to Lip Sync?",
      answer:
        "Lip Sync is immediately available to all Magic Hour users for free. You can access it in the free tool with no sign up required, or if you'd like more access, simply log in to your account and navigate to the Lip Sync tool in our video editing suite.",
    },
    {
      question: "What languages does the Lip Sync AI support?",
      answer:
        "Lip Sync AI supports all languages globally, making it the most versatile tool in the market. Whether you're creating content in English, Spanish, Mandarin, Japanese, Arabic, or any other language, our AI adapts seamlessly to match speech patterns and lip movements with exceptional precision and naturalness.",
    },
    {
      question: "Is there a limit to the length of the video that can be processed?",
      answer:
        "Currently, the maximum video length is 10 seconds in our free tool and 11 minutes (assuming 30 FPS) in our full Lip Sync tool. For longer projects, we recommend splitting your content into segments for optimal processing.",
    },
    {
      question: "What do I need to create a Lip Sync?",
      answer: (
        <div className="space-y-4">
          <p>To create a lip-sync video, you&apos;ll need:</p>
          <ul className="list-inside list-disc">
            <li>A video file (MP4 or MOV, up to 4K resolution)</li>
            <li>An audio file (MP3, WAV, or AAC)</li>
            <li>A stable internet connection to process the video in the cloud</li>
          </ul>
        </div>
      ),
    },
    {
      question: "Is the AI Lip Sync tool free to use?",
      answer: `Yes! We offer 3 free lip syncs in our free tool each day, ${signupFrames} frames for free if you sign up (about ${Math.floor(signupFrames / 24)} seconds at 24 FPS), and ${dailyFramesReward} free frames daily so you can experience Magic Hour and create your first Lip Sync video at no cost. To process additional frames or unlock advanced features, you can subscribe to our Creator, Pro, or Business plans or purchase a frame pack tailored to your needs.`,
    },
    {
      question: "Can I use Lip Sync for video translation?",
      answer:
        "Absolutely! Magic Hour's Lip Sync AI is the only technology that supports all languages worldwide. You can upload audio in any language, dialect, or accent, and our AI will perfectly synchronize the lip movements to match, making it ideal for content localization and global distribution.",
    },
    {
      question: "How does AI Lip Sync improve video dubbing and translation?",
      answer: (
        <div className="space-y-4">
          <p>
            Our AI can auto-translate and lip-sync videos by generating voiceovers in multiple
            languages while ensuring natural-looking lip movements. This makes it ideal for:
          </p>
          <ul className="list-inside list-disc">
            <li>Content localization (movies, YouTube, social media)</li>
            <li>E-learning & training videos</li>
            <li>Marketing & advertisements</li>
          </ul>
        </div>
      ),
    },
    {
      question: "What video formats and resolutions do you support?",
      answer:
        "Our platform supports professional-quality MOV or MP4 files up to 4K resolution. Both ungraded and graded footage work well with our system, supporting sRGB and Rec709 colorspaces. For optimal results, avoid using footage with text overlays on faces, fade-in transitions, or side profile shots.",
    },
    {
      question: "Do I need to download software to use the Lip Sync tool?",
      answer:
        "No, our Lip Sync AI runs entirely in your web browser with no downloads required. This cloud-based solution allows you to create professional lip-synced videos from any device with internet access.",
    },
    {
      question: "Can I lip sync videos using my own voice?",
      answer: (
        <div className="space-y-4">
          <p>Yes! You can either:</p>
          <ul className="list-inside list-disc">
            <li>Record your own audio separately and upload it to synchronize with your video.</li>
            <li>
              Use AI-generated voices or voice cloning in an online voice cloning tool for dubbing.
            </li>
          </ul>
        </div>
      ),
    },
    {
      question: "Does Lip Sync AI have an API?",
      answer: (
        <p>
          Yes! We offer a robust API for developers and businesses. Full documentation is available
          at{" "}
          <Link href="https://docs.magichour.ai" target="_blank" className="link">
            Magic Hour AI API Documentation
          </Link>{" "}
          allowing you to integrate our powerful lip sync capabilities directly into your
          applications or workflows. You can get started with the API for free.
        </p>
      ),
    },
    {
      question: "How can AI lip sync benefit my content creation?",
      answer: (
        <div className="space-y-4">
          <p>Beyond language translation, our Lip Sync AI enables:</p>
          <ul className="list-inside list-disc">
            <li>
              Dialogue replacement – Modify dialogue or replace entire scripts without reshooting.
            </li>
            <li>Script modifications – Adjust scripts for continuity edits.</li>
            <li>
              Content personalization – Create custom avatars and virtual presenters for marketing,
              education, or entertainment.
            </li>
            <li>
              Localization – Translate and sync videos into multiple languages for a global
              audience.
            </li>
            <li>
              Entertainment – Add fun voiceovers, create animated storytelling, or generate
              interactive social media content.
            </li>
          </ul>
        </div>
      ),
    },
    {
      question: "How accurate is the lip synchronization?",
      answer:
        "Our advanced AI delivers industry-leading accuracy in lip synchronization, creating results that are virtually indistinguishable from naturally recorded video. The system precisely matches mouth shapes, facial expressions, and even subtle speech mannerisms to create authentic, professional-quality results.",
    },
    {
      question: "Can I use Lip Sync AI for multiple speakers in one video?",
      answer:
        "Currently, our Lip Sync AI only supports single-speaker synchronization. However, we plan on adding multi-speaker synchronization in the future.",
    },
    {
      question: "What types of videos can I lip sync to audio?",
      answer: (
        <div className="space-y-4">
          <p>You can sync:</p>
          <ul className="list-inside list-disc">
            <li>Real human videos (talking head, interviews, vlogs)</li>
            <li>AI-generated avatars (for virtual influencers, marketing, and education)</li>
            <li>
              Animated characters (for lip-synced storytelling and dubbing). Make sure animated
              characters are “human-like” e.g. Pixar style, for best results.
            </li>
          </ul>
        </div>
      ),
    },
    {
      question: "Why choose Magic Hour's Lip Sync AI?",
      answer:
        "Unlike other tools, Magic Hour combines ease of use, advanced AI technology, and unparalleled realism to deliver professional-grade lip-syncing. Whether you're a solo creator or a large enterprise, our flexible pricing plans and robust features make it easy to bring your vision to life.",
    },
  ],
  photoColorizer: [
    {
      question: "What is the Photo Colorizer?",
      answer:
        "The Photo Colorizer takes black-and-white or faded photos and automatically adds realistic colors using AI. It analyzes the objects, faces, and context in the image to infer natural-looking tones. This makes it easy to revive old memories and bring historical photos to life with a single upload.",
    },
    {
      question: "Is the Photo Colorizer free to use?",
      answer:
        "Yes, the basic version is free to try. You can upload a photo and receive a colorized version with a watermark without needing to create an account. Paid users can download high-resolution images, remove the watermark, and skip the queue.",
    },
    {
      question: "How does the Photo Colorizer work?",
      answer:
        "The tool uses deep learning models trained on millions of images to predict accurate colors for grayscale photos. It identifies objects, people, and settings to apply realistic hues—like skin tones, sky, or clothing—while preserving fine details. The process is fully automated and takes just a few seconds.",
    },
    {
      question: "What kinds of photos work best?",
      answer:
        "The Photo Colorizer performs best on clear, well-lit black-and-white portraits or outdoor scenes. Photos with recognizable objects and faces typically produce the most accurate colorizations. Blurry, heavily damaged, or abstract images may still work, but with less consistent results.",
    },
    {
      question: "Can I fine-tune the colors after colorization?",
      answer:
        "Not yet. The current version focuses on fully automatic results for speed and simplicity. If you're looking for more customization, you can use our photo editing tools to adjust the style or make enhancements afterward.",
    },
    {
      question: "Is my photo data stored or reused?",
      answer:
        "No. Uploaded photos are processed securely and deleted shortly after use. We don't store your images for training or share them with any third parties. Your content remains private and protected.",
    },
    {
      question: "Do you limit the number of images I can restore?",
      answer:
        "Free users are limited to a small number of photo colorizations per day. If you hit the limit, you can either wait or sign up for a free account, which will give you access to our full product. If you upgrade to a paid plan, you'll also get additional benefits like higher resolution, faster speeds, and no watermark, ideal for larger projects or ongoing use.",
    },
    {
      question: "Why retouch an old portrait with Magic Hour's Photo Colorizer?",
      answer:
        "Color brings emotion and realism back to old portraits. Our AI enhances skin tones, background colors, and fine details to make the image feel alive again. It's an easy way to reconnect with family history or give new life to old memories. The results are often striking, even with minimal effort.",
    },
    {
      question: "How can I enlarge old photos without losing details?",
      answer:
        "Use the AI Image Upscaler, which enhances resolution up to 4x while preserving detail. It's designed to retain textures, facial features, and sharpness. This is useful for printing, archiving, or sharing restored images online. It works especially well after colorizing your photo.",
    },
    {
      question: "Can I use colorized photos for commercial projects?",
      answer:
        "Yes, paid users can use their results for commercial purposes. This includes publishing, prints, marketing materials, and more. Free versions are for personal use and include a watermark. Upgrade for full resolution and usage rights.",
    },
    {
      question: "What file types are supported?",
      answer:
        "We support JPG, PNG, and WebP formats. If your image is in another format like TIFF or BMP, use a free converter before uploading. For best results, upload clear, high-quality images at 512x512 resolution or higher.",
    },
  ],
  textToVideo: [
    {
      question: "How do I get access to Text-to-Video?",
      answer: `Currently text-to-video is only available to subscribers to our ${planText}s.`,
    },
    {
      question: "How much does Text-to-Video cost?",
      answer: `The amount of videos you can make depends on your plan. Each second of video in text-to-video costs ${costMultiplierByType.TEXT_TO_VIDEO * fps} frames, so if you purchase a Creator monthly plan, you can create ${Math.floor(creatorMonthlyFrames / (5 * costMultiplierByType.TEXT_TO_VIDEO * fps))} five second videos per month.`,
    },
    {
      question: "How do I create a text-to-video video?",
      answer: (
        <>
          To create a text-to-video video, go to{" "}
          <a href="https://magichour.ai/create/text-to-video" className="link">
            https://magichour.ai/create/text-to-video
          </a>{" "}
          and enter a prompt!
        </>
      ),
    },
    {
      question: "What should I include in my prompt?",
      answer:
        "In general, you'll get better results by being more specific. Some of the things we recommend including in your prompt are the camera motion, e.g. “A drone fly through,” an action, e.g. “a man running,” and details about the setting, people, and objects in your scene.",
    },
    {
      question: "How long do videos take to generate?",
      answer:
        "We provide an ETA after you start your video render, but as a rule of thumb, each 5 second video requires roughly 5-6 minutes to generate. ",
    },
    {
      question: "My video ran into an error. What do I do?",
      answer:
        "Try changing the prompt and trying again later. Currently we have a safety system that doesn’t allow generations with certain words, so you might have better results if you shorten your prompt or remove any words that may violate our content policy.",
    },
  ],
  videoToVideo: [
    {
      question: "How much does Video-to-Video cost?",
      answer: `Each image in the video costs ${costMultiplierByType.VIDEO_TO_VIDEO} frames.`,
    },
    {
      question: "How do I access Video-to-Video?",
      answer: `Video-to-Video is only available to users with an active ${planText}.`,
    },
    {
      question: "Can I upload my own initial video?",
      answer: "Yes you can! We support uploading your own video or using a YouTube URL.",
    },
    {
      question: "Can I have multiple styles throughout the video?",
      answer:
        "To achieve multiple styles, you will need to create multiple videos with the different styles you're looking for. We will be adding a built-in feature for dynamic styles in the future.",
    },
    {
      question: "How do I input a style?",
      answer:
        "To input a style, write a prompt with the style, subjects, and art style you're looking for. We also have preset art style templates that you can choose to augment your video.",
    },
  ],
  aiMemeGenerator: [
    {
      question: "What is an AI Meme Generator?",
      answer:
        "An AI meme generator uses artificial intelligence to automatically create memes by pairing images with relevant, often humorous captions. You can either upload your own image or choose from popular meme templates. The AI analyzes the image and generates a caption that matches its tone or context. It's a fast, fun way to create shareable content without needing design or writing skills.",
    },
    {
      question: "Is the AI Meme Generator free?",
      answer:
        "Yes, our AI meme generator is free to use with basic features. You can generate memes instantly without needing to sign up. For more usage, history, and no watermark, we offer a premium version. The free version is designed to be fast, accessible, and easy to try.",
    },
    {
      question: "How do I generate memes using AI?",
      answer:
        "Pick a meme template and write a short prompt describing what the meme should be about. The AI will generate a funny or relevant caption based on your input. You can edit the caption if you like, then download or share the meme instantly—no design skills required.",
    },
    {
      question: "How is this meme generator different from others?",
      answer:
        "Most meme generators require you to write your own captions. Ours uses AI to turn a simple prompt into a funny, context-aware caption that fits a popular meme format. It lowers the barrier to creativity and makes meme creation faster, easier, and more fun.",
    },
    {
      question: "How does AI meme generation work?",
      answer:
        "The AI reads your prompt and analyzes the chosen meme template to generate a caption that fits the format and tone. It draws on patterns from internet culture, trending memes, and humor data to craft something that feels relevant and entertaining. The entire process takes just seconds.",
    },
    {
      question: "How do I make a good meme?",
      answer:
        "Pick a well-known meme template and write a prompt that captures a relatable, ironic, or exaggerated scenario. The best memes are simple, surprising, and emotionally resonant—whether they're funny, frustrating, or painfully true. Let the AI do the rest.",
    },
    {
      question: "Why use an AI meme generator?",
      answer:
        "It makes meme creation accessible to anyone, even if you're not a designer or writer. Just describe an idea, and the AI handles the caption and formatting. It's fast, effortless, and ideal for creating content that entertains, engages, or goes viral.",
    },
  ],
  aiGifGenerator: [
    {
      question: "What is the AI GIF Generator?",
      answer:
        "The AI GIF Generator lets you create animated GIFs from text prompts or images using generative AI. You can describe a scene—like “a cat floating through space in pixel art style”—and get a looping GIF within seconds. It's designed for creators, marketers, and anyone who wants to make eye-catching content without animation skills.",
    },
    {
      question: "Is the AI GIF Generator free to use?",
      answer:
        "Yes, you can try the tool for free. Free users can generate a limited number of GIFs per day, with a watermark and standard resolution. For more features—like high-res downloads, no watermark, and priority access—you can upgrade to a paid plan.",
    },
    {
      question: "How does the AI GIF Generator work?",
      answer:
        "The tool uses AI models trained on large animation and image datasets to generate a series of frames based on your input prompt or uploaded image. These frames are then stitched into a smooth, looping GIF. The process is fully automated and typically takes less than a minute.",
    },
    {
      question: "What kind of prompts work best?",
      answer:
        "Clear, descriptive prompts with visual detail tend to produce the best results. For example, “a robot dancing under neon lights in anime style” will yield better results than just “robot dancing.” You can also add style cues like “3D,” “pixel art,” or “cinematic lighting” for more control.",
    },
    {
      question: "Can I upload an image to guide the animation?",
      answer:
        "Yes. You can upload an image to serve as the first frame or stylistic reference for your GIF. This helps the AI anchor the generation around a specific character, subject, or look. It's optional, but useful for more personalized results.",
    },
    {
      question: "What styles can I create with the AI GIF Generator?",
      answer:
        "You can generate GIFs in a wide range of styles, including anime, cinematic, pixel art, sketch, digital painting, and more. The tool will continue to evolve with new style presets and prompt-based fine-tuning, making it easier to match your aesthetic.",
    },
    {
      question: "Can I use the generated GIFs commercially?",
      answer:
        "Yes, paid users have full rights to use their GIFs in commercial projects. This includes social media, marketing, websites, and client work. Free users can use GIFs for personal, non-commercial purposes and must credit the tool or keep the watermark.",
    },
    {
      question: "Are there limits on how many GIFs I can make?",
      answer:
        "Free users can generate a small number of GIFs each day. If you reach the limit, you can wait for a reset or sign up for a free account to unlock more usage. Paid users get unlimited or higher daily limits, faster speeds, and enhanced export options.",
    },
    {
      question: "Will my prompts or images be stored or reused?",
      answer:
        "No. Your data is processed securely and deleted shortly after generation. We don't use your inputs to train models or share them with third parties. Your privacy and creative control are important to us.",
    },
    {
      question: "What formats can I download the GIF in?",
      answer:
        "By default, your animation is available as a standard GIF file. Paid users can also export as high-quality MP4 for better compression and social media compatibility. All files are optimized for sharing on platforms like Instagram, TikTok, and Discord.",
    },
  ],
  aiImageGenerator: [
    {
      question: "How do I generate an image from text?",
      answer:
        "Type your prompt into the text field and click 'Generate' to instantly create multiple AI-generated images in seconds. Our system processes your description and transforms it into visual content based on your specifications. Once generated, you can easily download your favorite images in high resolution and share them directly from the platform.",
    },
    {
      question: "What is the best AI image generator?",
      answer:
        "There are now many impressive AI image generators including Midjourney, Leonardo, and Magic Hour, each with their own strengths. Midjourney excels at artistic renders, while Leonardo offers advanced customization features. Magic Hour stands out by offering a combination of quality, speed, and accessibility in a free platform. All products allow you to generate professional-quality AI images and are accessible to anyone without technical barriers.",
    },
    {
      question: "How can I get AI generated images for free?",
      answer:
        "Magic Hour's AI image generator is completely free to use with no hidden costs or subscription requirements. Unlike many platforms that limit free users to a small number of generations, we provide generous daily limits without requiring account creation or payment information. This makes Magic Hour the perfect solution for creators, students, and professionals who need quality AI-generated imagery without budget constraints.",
    },
    {
      question: "Do I own my AI generated images?",
      answer:
        "All images you generate using Magic Hour's AI picture generator belong entirely to you and can be used for both personal and commercial projects. We make no copyright claims on your generated content, giving you full freedom to use, modify, and distribute your creations. However, we recommend consulting local copyright laws regarding AI-generated content, as regulations vary by jurisdiction and are still evolving in many regions.",
    },
    {
      question: "How do I write a good prompt?",
      answer: (
        <>
          Effective prompts combine specific subject matter with style descriptors, technical
          parameters, and reference points. Start with a clear subject (e.g., &quot;A serene
          mountain lake at sunset&quot;), then add artistic style (&quot;in the style of
          impressionist painting&quot;), technical details (&quot;sharp focus, dramatic lighting, 8K
          resolution&quot;), and reference artists or mediums if desired (&quot;similar to Ansel
          Adams photography&quot;). For best results, experiment with prompt structures like
          &quot;Subject + Setting + Lighting + Style + Quality&quot; or &quot;Adjective + Subject +
          Action + Environment + Time of Day + Perspective&quot;. We&apos;ve also written an{" "}
          <Link href="/blog/how-to-prompt-ai-videos-a-simple-guide" className="link">
            AI prompting guide
          </Link>{" "}
          with example prompts and advanced techniques to help you create stunning images!
        </>
      ),
    },
    {
      question: "Why use an AI image generator?",
      answer:
        "An AI image generator dramatically reduces the time and technical skills needed to create professional-quality visuals, allowing you to transform concepts into compelling imagery instantly. It serves as a powerful brainstorming tool, helping overcome creative blocks by visualizing ideas that might be difficult to articulate through traditional methods. Magic Hour makes this technology accessible to everyone, enabling you to produce eye-catching content for social media, websites, presentations, and design projects without specialized design knowledge.",
    },
    {
      question: "How are AI-generated images created?",
      answer:
        "AI-generated images are created through sophisticated machine learning models trained on millions of images and their associated text descriptions from across the internet. These models learn to recognize patterns between text prompts and visual elements, understanding concepts like objects, styles, lighting, and composition. When you enter a prompt, Magic Hour's AI analyzes your text, interprets the concepts you've described, and generates entirely new images that match your specifications without copying existing artwork.",
    },
    {
      question: "How many images can I generate daily?",
      answer:
        "Magic Hour allows you to generate up to 20 images daily completely free in our free tool with no signup needed. If you sign up, you get access to 80 additional free images instantly, and an additional 20 free images daily by claiming your daily credits. This generous limit ensures you can explore creative concepts and complete projects without hitting frustrating barriers. For users with more extensive needs, our affordable premium tiers offer increased or unlimited generations along with additional features like higher resolution outputs, priority processing, and advanced customization options.",
    },
    {
      question: "Can I use Magic Hour's AI images for commercial projects?",
      answer:
        "Yes, you can use images created with Magic Hour for commercial projects including marketing materials, product designs, website content, and merchandise. We grant users full usage rights to their generated images without royalty requirements or attribution. However, as with any design element, ensure your final products don't infringe on existing trademarks or incorporate recognizable copyrighted characters, celebrities, or brands without appropriate permissions.",
    },
    {
      question: "What makes a great AI image prompt?",
      answer:
        "Great AI prompts balance specificity with creative freedom, giving the AI clear direction while allowing artistic interpretation. Begin with concrete subjects and scenarios, then layer in stylistic elements, lighting conditions, color palettes, and technical specifications. Avoid overly complex or contradictory instructions, and remember that quality often trumps quantity—a focused 15-word prompt often produces better results than a rambling paragraph. Experiment with both minimal prompts ('watercolor painting of mountains') and detailed descriptions to discover what works best for your specific vision.",
    },
    {
      question: "Is Magic Hour suitable for beginners?",
      answer:
        "Magic Hour is specifically designed to be accessible and intuitive for beginners while offering depth for experienced users. Our streamlined interface eliminates technical barriers, allowing anyone to create professional-quality images with simple text prompts. We provide helpful suggestions, pre-made prompt templates, and a comprehensive beginner's guide to help new users achieve impressive results immediately. Unlike complex design software or other AI platforms, Magic Hour requires no technical knowledge or training period.",
    },
    {
      question: "Can I edit or modify my AI-generated images?",
      answer:
        "Magic Hour allows you to regenerate variations of your favorite images with adjusted prompts for iterative refinement. For more extensive editing, you can easily download your generated images in high resolution and modify them in any image editing software. This workflow combines the speed and inspiration of AI generation with the precision of traditional editing tools, giving you complete creative control over your final images while significantly reducing the time spent on initial creation.",
    },
    {
      question: "What image styles can Magic Hour generate?",
      answer:
        "Magic Hour can generate an incredibly diverse range of visual styles including photorealism, digital art, oil paintings, watercolors, pencil sketches, anime, 3D renders, concept art, and abstract compositions. The AI excels at combining different artistic influences, allowing you to create unique hybrid styles like 'cyberpunk watercolor' or 'art deco photography.' By specifying artists, movements, time periods, or technical parameters in your prompt, you can guide the AI toward virtually any aesthetic direction you can imagine.",
    },
    {
      question: "What makes Magic Hour's AI image generator unique?",
      answer:
        "Magic Hour sets itself apart with a free, no-sign-up platform that’s fast, intuitive, and open to all, unlike many tools that require payment or accounts. Our AI is regularly updated to deliver top-notch image quality, and our user-friendly design caters to both beginners and experts. We also provide resources like a prompting guide to help users maximize their creative potential.",
    },
    {
      question: "What types of projects are best suited for AI-generated images?",
      answer:
        "AI-generated images from Magic Hour are ideal for diverse projects like social media graphics, blog visuals, marketing campaigns, and even conceptual art or storyboards. They’re great for quickly prototyping ideas, mocking up designs, or adding unique flair to personal creations. The speed and versatility make them a go-to for anyone needing eye-catching visuals fast.",
    },
    {
      question: "How can I ensure my AI-generated images align with my brand?",
      answer:
        "To align images with your brand, include your brand’s colors, style, and vibe in your prompts, such as 'a sleek logo in red and black with a modern tech feel.' Keep a log of prompts that work well to reuse later, ensuring consistency across your visuals. Clear, brand-specific details guide the AI to produce images that fit your identity perfectly.",
    },
    {
      question: "Are there any limitations to what the AI can generate?",
      answer:
        "While Magic Hour’s AI is robust, it may struggle with highly abstract ideas, intricate details, or niche artistic styles if prompts lack clarity. You can improve results by refining your descriptions and avoiding overly vague inputs—specificity is key. With practice, most users find they can achieve impressive outcomes for a wide range of concepts.",
    },
    {
      question: "How does Magic Hour ensure the safe use of its AI image generator?",
      answer:
        "Magic Hour promotes safe use by automatically screening prompts to block inappropriate content and maintain a respectful platform. We encourage users to report any issues they encounter, helping us keep the community positive and secure. Our terms of service also set clear guidelines to ensure ethical and responsible use of the tool.",
    },
  ],
  aiImageUpscaler: [
    {
      question: "What is an AI photo enhancer?",
      answer:
        "An AI photo enhancer is a tool that increases the resolution of low-quality images, enhancing their size and clarity without losing picture quality.",
    },
    {
      question: "How do I upscale an image with Magic Hour?",
      answer: (
        <>
          1. Upload Your Image - Select the image you want to upscale.
          <br />
          2. Let AI Enhance It - Our AI will start enhancing the resolution while preserving
          details.
          <br />
          3. Download - Once complete, download your high-resolution image.
        </>
      ),
    },
    {
      question: "Is Magic Hour's AI image enhancer free to use?",
      answer: (
        <>
          Yes, Magic Hour&apos;s AI image enhancer is free to use. However, advanced features and
          higher resolution outputs are available for premium users. 2x enhancement is available for
          free users, 4x enhancement is available for users with a {planText}. Get pricing details{" "}
          <Link href="/pricing" className="link">
            here
          </Link>
          .
        </>
      ),
    },
    {
      question: "How does Magic Hour's AI image enhancer work?",
      answer:
        "Our AI image enhancer uses AI to analyze your image and generate a higher-resolution version by enhancing the textures, sharpness, and details of your image.",
    },
    {
      question: "What is the maximum upscale factor supported by Magic Hour?",
      answer:
        "Magic Hour supports upscaling images up to 4x the original resolution for paid users and 2x the original resolution for free users.",
    },
    {
      question: "Can I use upscaled images for commercial purposes?",
      answer:
        "Yes, any images you create on Magic Hour are yours and can be used for whatever you want.",
    },
  ],

  aiHeadshotGenerator: [
    {
      question: "How do I generate a headshot using an AI headshot generator?",
      answer:
        "Simply upload a clear selfie, and our AI will automatically enhance, refine, and generate a professional-quality headshot. Using advanced image processing, the tool applies background removal, lighting adjustments, and facial enhancements to create a polished and realistic result. In just a few seconds, you'll receive a high-resolution headshot ideal for professional profiles, resumes, social media, and more—no need for an expensive studio photoshoot!",
    },
    {
      question: "What kind of image should I use?",
      answer:
        "You should use an image that has a clear shot of your face with no obstructions. It should also resemble you, be high resolution, and not include multiple people.",
    },
    {
      question: "How can I get AI generated headshots for free?",
      answer:
        "Magic Hour's AI headshot generator is free to use. No need to sign up, start a free trial, or enter a credit card!",
    },
    {
      question: "Do I own my AI generated images?",
      answer:
        "The images you generate using Magic Hour's AI headshot generator belong to you and you only. We do not store your output images or input images.",
    },
    {
      question: "How do I regenerate my headshot if I'm not satisfied?",
      answer:
        "Simply click regenerate or upload another photo and create another headshot for free! You can keep creating headshots until you're fully satisfied with your output.",
    },
    {
      question: "Why use an AI headshot generator?",
      answer:
        "An AI headshot generator can create a professional headshot in about 30 seconds for free, compared to hundreds of dollars and hours of your time for a photographer",
    },
  ],
  aiQrCodeGenerator: [
    {
      question: "What is a QR code?",
      answer:
        "A QR code (Quick Response Code) is a type of barcode that can store a wide variety of data, like URLs, which can be quickly scanned using a smartphone camera. Our AI-generated QR codes take this basic technology to the next level by turning your QR codes into personalized, artistic designs that reflect your brand or creative vision.",
    },
    {
      question: "Can I use the QR Codes for commercial purposes?",
      answer:
        "Yes, any QR codes you create on Magic Hour are yours and can be used for whatever you want.",
    },
    {
      question: "Do the created QR codes expire?",
      answer:
        "No, Magic Hour's AI-generated QR codes never expire. They are static and will work indefinitely once created, allowing you to use them as long as you need.",
    },
    {
      question: "Is there a scan limit for the QR codes?",
      answer:
        "There's no scan limit on the QR codes you generate with Magic Hour. They can be scanned as many times as necessary and will always direct users to your chosen URL or content.",
    },
    {
      question: "Why isn't my QR code working properly?",
      answer:
        "First, try zooming your camera in and out and left and right. Then, try re-generating your QR code. Lastly, check the data you entered for accuracy, and ensure you're using a reliable QR code scanner app.",
    },
    {
      question: "How many QR codes I can generate?",
      answer: `Magic Hour users can generate up to 5 AI-generated QR codes per day completed free. If you would like to generate more QR codes, sign up for an account to get ${Math.floor(signupFrames / frameCostPerQrCode)} free QR codes, plus ${Math.floor(dailyFramesReward / frameCostPerQrCode)} additional generations each day.`,
    },
  ],
  autoSubtitleGenerator: [
    {
      question: "What is an auto subtitle generator?",
      answer:
        "An auto subtitle generator is a tool that creates text captions for videos. Subtitles are a video's dialogue in text form, and allow viewers to follow the video with no audio.",
    },
    {
      question: "How do I add subtitles to a video?",
      answer:
        "Simply upload the video and let our AI do the work for you. Our product automatically detects dialogue, transcribes it, and generates the subtitles in seconds.",
    },
    {
      question: "Is the auto subtitle generator free?",
      answer:
        "Yes, Magic Hour's auto subtitle generator is free with no sign up required. However, additional features are available for signed up users, as well as subscribers.",
    },
    {
      question: "Can subtitles be generated in more than one language at a time?",
      answer:
        "Yes, our subtitle generator works with over 50 languages, and detects the languages automatically, even within the same video.",
    },
    {
      question: "How can I change the style of my subtitles?",
      answer:
        "You can change the subtitle styles by selecting one of our templates. Access more customization by signing up and using our full auto subtitle generator product.",
    },
    {
      question: "How do I edit my subtitles?",
      answer:
        "To edit your subtitle, use our full subtitle generator product by signing up, which allows you to edit, delete, and add individual words (coming soon).",
    },
  ],
  imageBackgroundRemover: [
    {
      question: "What is an image background remover?",
      answer:
        "An image background remover is a tool that detects and removes the background of an image, resulting in a transparent background.",
    },
    {
      question: "How do I remove the background from an image?",
      answer:
        "Simply upload the image and let our AI do the work for you. Our product automatically detects and removes the background from your photo for you to download or edit after.",
    },
    {
      question: "Is the image background remover free?",
      answer:
        "Yes, Magic Hour's image background remover is free. However, advanced features and higher resolution outputs are available for premium users.",
    },
    {
      question: "How can I change the background color?",
      answer:
        "After uploading an image and removing the background, you can select from our preset background colors or a custom color to replace as the background. ",
    },
    {
      question: "How do I change the background of an image?",
      answer:
        "After you remove the background, you can upload an image to replace as the background, or choose from one of our preset backgrounds.",
    },
    {
      question: "Can I use batch remove backgrounds from images?",
      answer:
        "Yes, you can remove backgrounds from up to 50 images simultaneously by using our full product or API. To get access, simply sign up with a free account.",
    },
  ],
  aiTalkingPhoto: [
    {
      question: "What is an AI Talking Photo?",
      answer:
        "An AI Talking Photo is a digital avatar created from an uploaded image that speaks naturally, based on your provided script. It's ideal for online courses, presentations, marketing videos, and engaging content across multiple platforms.",
    },
    {
      question: "Is creating an AI Talking Photo free?",
      answer:
        "Yes, Magic Hour allows you to create talking photos for free. Simply sign up and log in to your account to turn your images into talking photos with professional voiceovers at no cost.",
    },
    {
      question: "Can I use any image to create a talking photo?",
      answer:
        "You can animate any image with a clearly visible face—including human portraits, cartoons, or animal images. If you're unsure which image to choose, try our preset avatars or create a custom AI avatar using text.",
    },
    {
      question: "Can I use AI Talking Photos commercially?",
      answer:
        "Yes, all AI Talking Photos created with Magic Hour can be used commercially as long as you own the image and audio used. You can easily make tutorial videos, explainer videos, marketing materials, and more, without restrictions.",
    },
    {
      question: "What is the maximum video length for AI Talking Photos?",
      answer:
        "Magic Hour's free AI Talking Photo tool currently supports up to 5 seconds of video in length. For longer videos, sign up for a free account to access extended video durations.",
    },
    {
      question: "Can I lip-sync audio to an existing video?",
      answer:
        "Yes. Magic Hour offers a free Lip Sync Generator that accurately synchronizes audio to the lip movements in your videos, creating realistic lip-synced video content.",
    },
    {
      question: "Which languages does Magic Hour support for Talking Photos?",
      answer:
        "Magic Hour's technology is language-independent and designed to work with all spoken languages. Your talking photo can speak fluently regardless of the language you use.",
    },
    {
      question: "How many faces can I animate per photo?",
      answer: "Currently, Magic Hour supports animating one face per photo.",
    },
  ],
  aiFaceEditor: [
    {
      question: "What is the AI Face Editor?",
      answer:
        "The AI Face Editor lets you upload a face photo and fine-tune its expression using a set of detailed sliders. You can adjust facial parameters like gaze, mouth shape, and head tilt to construct the exact look or emotion you want. It's ideal for creators who want control over facial expressions without needing design or Photoshop skills.",
    },
    {
      question: "Is the AI Face Editor free to use?",
      answer:
        "Yes, the basic version is free and lets you upload a photo, edit expressions, and download the result with a watermark. You don’t need to sign up to try it. A premium version unlocks higher-resolution downloads, removes watermarks, and allows you to skip the queue.",
    },
    {
      question: "How does the AI Face Editor work?",
      answer:
        "When you upload a face image, the AI detects key landmarks and gives you sliders to adjust each part of the face—like eyebrow tilt, eye openness, lip shape, and head position. You can combine these to simulate expressions such as smiling, frowning, or surprise. All edits are non-destructive and update in real time.",
    },
    {
      question: "Can it generate emotions automatically?",
      answer:
        "No. The tool doesn't apply emotions like “happy” or “sad” directly. Instead, it gives you precise control over facial components so you can build expressions yourself. This allows for more creative freedom and subtlety compared to automatic filters or presets.",
    },
    {
      question: "What kinds of edits can I make?",
      answer:
        "You can control over a dozen parameters, including eyebrow direction, eye gaze, lip openness, mouth curvature, and head tilt. These allow you to subtly or dramatically alter a face’s expression or pose. Whether you're aiming for a slight smirk or an exaggerated reaction, the editor gives you full flexibility.",
    },
    {
      question: "Can I animate or bring static images to life?",
      answer: (
        <>
          While it doesn’t animate images frame-by-frame, the tool lets you make static portraits
          feel more dynamic by adjusting expressions and posture. However, you can use our{" "}
          <Link href="/products/image-to-video" className="link">
            image-to-video
          </Link>{" "}
          or{" "}
          <Link href="/products/ai-talking-photo" className="link">
            talking photo
          </Link>{" "}
          products to turn your portrait into a video, ideal for avatars, storytelling, and personal
          messages.
        </>
      ),
    },
    {
      question: "What makes this different from other face editing tools?",
      answer:
        "Unlike apps that use prebuilt filters or templates, this editor gives you granular, real-time control over how each facial feature behaves. That means you’re not limited to one-click effects—you can experiment, combine, and refine facial changes exactly how you want, just like a video game character builder screen. It’s built for precision and creativity.",
    },
    {
      question: "Can I edit any type of face or image?",
      answer:
        "The editor works best with clear, front-facing photos of a single person. It supports a variety of faces, but results are more accurate when facial features are unobstructed and well-lit. Stylized or artistic images may work, but with reduced precision.",
    },
    {
      question: "Do I need any technical skills to use this?",
      answer:
        "No. The interface is simple and slider-based, so anyone can explore and edit expressions with no learning curve. You’ll see instant visual feedback, making it easy to understand how each parameter affects the face.",
    },
    {
      question: "Is my photo data safe?",
      answer:
        "Yes. Uploaded images are processed securely and are deleted after use. We don’t use your images for training or share them with third parties. Your data stays private, safe, and secure.",
    },
  ],
};
